'use client';

import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useEffect, useRef, useState, useCallback } from 'react';

export interface OrderNotification {
  order_code: string;
  store_name: string;
  order_status: string;
  total_payment: number;
  time: number;
}

export enum SSEConnectionStatus {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  ERROR = 'error',
}

interface UseOrderNotificationOptions {
  token: string;
  enabled?: boolean;
  onMessage?: (data: OrderNotification) => void;
  onError?: (error: Error) => void;
  reconnectInterval?: number; // Base reconnect interval in ms
  maxReconnectInterval?: number; // Max reconnect interval in ms
  maxRetryAttempts?: number; // Maximum number of retry attempts
}

export function useOrderNotificationSSE({
  token,
  enabled = true,
  onMessage,
  onError,
  reconnectInterval = 3000, // 3 seconds base delay
  maxReconnectInterval = 60000, // 60 seconds max delay
  maxRetryAttempts = 10, // Stop after 10 failed attempts
}: UseOrderNotificationOptions) {
  const [messages, setMessages] = useState<OrderNotification[]>([]);
  const [status, setStatus] = useState<SSEConnectionStatus>(
    SSEConnectionStatus.DISCONNECTED
  );
  const [error, setError] = useState<Error | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isManuallyClosedRef = useRef(false);
  const isConnectingRef = useRef(false); // Prevent multiple simultaneous connections

  // Stable refs for callbacks to prevent re-creating connect function
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  /**
   * Calculate reconnect delay with exponential backoff
   */
  const getReconnectDelay = useCallback(() => {
    return Math.min(
      reconnectInterval * Math.pow(2, reconnectAttemptsRef.current),
      maxReconnectInterval
    );
  }, [reconnectInterval, maxReconnectInterval]);

  /**
   * Close the SSE connection
   */
  const closeConnection = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    isConnectingRef.current = false;
  }, []);

  /**
   * Handle incoming messages with duplicate prevention
   */
  const handleMessage = useCallback((data: OrderNotification) => {
    setMessages((prev) => {
      // Prevent duplicate notifications
      const isDuplicate = prev.some(
        (m) => m.order_code === data.order_code && m.time === data.time
      );
      if (isDuplicate) return prev;
      return [...prev, data];
    });

    // Call notification handler if provided using stable ref
    onMessageRef.current?.(data);
  }, []);

  /**
   * Connect to SSE endpoint using fetchEventSource
   */
  const connect = useCallback(() => {
    // Don't connect if disabled, no token, or manually closed
    if (!enabled || !token || isManuallyClosedRef.current) {
      return;
    }

    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      console.log('[SSE Order] Already connecting, skipping duplicate attempt');
      return;
    }

    // Close existing connection
    closeConnection();

    // Mark as connecting
    isConnectingRef.current = true;

    // Create new AbortController for this connection
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setStatus(SSEConnectionStatus.CONNECTING);
    setError(null);

    // Start SSE connection
    fetchEventSource(
      process.env.NEXT_PUBLIC_ORDER_NOTIFICATION_API ??
        'https://api.ahamove.com/api/v3/private/food-order/customer/order-notification',
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        },
        signal: abortController.signal,

        async onopen(response) {
          await Promise.resolve(); // Satisfy lint rule for await expression

          if (response.ok) {
            console.log('[SSE Order] Connection established');
            setStatus(SSEConnectionStatus.CONNECTED);
            reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
            isConnectingRef.current = false; // Connection successful
          } else if (
            response.status >= 400 &&
            response.status < 500 &&
            response.status !== 429
          ) {
            // Client error (4xx) - don't retry except for 429 (rate limit)
            console.error(
              '[SSE Order] Fatal client error - will not retry:',
              response.status,
              response.statusText
            );
            const clientError = new Error(
              `Fatal client error: ${response.status} ${response.statusText}`
            );
            setError(clientError);
            setStatus(SSEConnectionStatus.ERROR);
            isConnectingRef.current = false;

            onErrorRef.current?.(clientError);

            // Mark as manually closed to prevent further retries
            isManuallyClosedRef.current = true;

            // Throw to enter onerror and stop retrying
            throw clientError;
          } else {
            // Server error (5xx) or other - will retry
            console.error(
              '[SSE Order] Server error:',
              response.status,
              response.statusText
            );
            const serverError = new Error(
              `Server error: ${response.status} ${response.statusText}`
            );
            setError(serverError);
            setStatus(SSEConnectionStatus.ERROR);
            isConnectingRef.current = false;

            onErrorRef.current?.(serverError);

            // Will retry automatically
            throw serverError;
          }
        },

        onmessage(event) {
          // Skip empty messages or heartbeats
          if (!event.data || event.data.trim() === '') {
            return;
          }

          try {
            const notification = JSON.parse(event.data) as OrderNotification;
            console.log('[SSE Order] Notification received:', notification);
            handleMessage(notification);
          } catch (err) {
            console.error('[SSE Order] Error parsing notification:', err);
            console.error(
              '[SSE Order] Raw data that failed to parse:',
              event.data
            );

            const parseError = new Error(
              `Failed to parse notification: ${
                err instanceof Error ? err.message : 'Unknown error'
              }`
            );
            setError(parseError);
            onErrorRef.current?.(parseError);
          }
        },

        onerror(err) {
          console.error('[SSE Order] Connection error:', err);
          isConnectingRef.current = false;

          const connectionError =
            err instanceof Error ? err : new Error('SSE connection error');
          setError(connectionError);
          setStatus(SSEConnectionStatus.ERROR);

          onErrorRef.current?.(connectionError);

          // If manually closed, throw to stop retrying
          if (isManuallyClosedRef.current) {
            console.log(
              '[SSE Order] Stopping retry loop (manual close or fatal error)'
            );
            throw connectionError;
          }

          // Update reconnect attempts
          reconnectAttemptsRef.current += 1;

          // Check if we've exceeded max retry attempts
          if (reconnectAttemptsRef.current >= maxRetryAttempts) {
            console.error(
              `[SSE Order] Max retry attempts (${maxRetryAttempts}) reached - stopping retries`
            );
            setStatus(SSEConnectionStatus.ERROR);
            isManuallyClosedRef.current = true; // Prevent further retries
            throw new Error(
              `Max retry attempts (${maxRetryAttempts}) exceeded`
            );
          }

          // Log retry attempt
          const delay = getReconnectDelay();
          console.log(
            `[SSE Order] Will retry connection (attempt ${reconnectAttemptsRef.current}/${maxRetryAttempts}) in ~${delay}ms...`
          );

          // fetchEventSource will automatically retry with exponential backoff
          // unless we throw an error here.
        },

        onclose() {
          console.log('[SSE Order] Connection closed');
          setStatus(SSEConnectionStatus.DISCONNECTED);
          isConnectingRef.current = false;

          // Clean up abort controller
          abortControllerRef.current = null;
        },

        // fetchEventSource built-in retry configuration
        openWhenHidden: true, // Keep connection alive when tab is hidden
      }
    ).catch((err) => {
      // Final catch for any unhandled errors
      isConnectingRef.current = false;
      if (err.name !== 'AbortError') {
        console.error('[SSE Order] Unhandled error:', err);
      }
    });
  }, [
    enabled,
    token,
    closeConnection,
    handleMessage,
    getReconnectDelay,
    maxRetryAttempts,
  ]);

  /**
   * Manual reconnect function
   */
  const reconnect = useCallback(() => {
    isManuallyClosedRef.current = false;
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  /**
   * Handle tab visibility changes
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log(
          '[SSE Order] Tab hidden, connection will be maintained by fetchEventSource'
        );
      } else {
        console.log('[SSE Order] Tab visible again');

        // Check if we need to reconnect
        if (
          status === SSEConnectionStatus.DISCONNECTED ||
          status === SSEConnectionStatus.ERROR
        ) {
          console.log('[SSE Order] Reconnecting after tab became visible');
          reconnect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [reconnect, status]);

  /**
   * Initialize connection when enabled and token is available
   */
  useEffect(() => {
    if (enabled && token) {
      isManuallyClosedRef.current = false;
      connect();
    } else {
      isManuallyClosedRef.current = true;
      closeConnection();
      setStatus(SSEConnectionStatus.DISCONNECTED);
    }

    // Cleanup on unmount
    return () => {
      isManuallyClosedRef.current = true;
      closeConnection();
    };
  }, [enabled, token, connect, closeConnection]);

  return {
    messages,
    status,
    error,
    reconnect,
  };
}
